import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Eye, EyeOff, Save } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import type { ContentStatus } from '@jeraisonne/db';
import { useAdminLessonDetail } from '@/lib/admin/queries';
import { useUpdateLesson, slugify } from '@/lib/admin/mutations';
import { MarkdownMath } from '@jeraisonne/ui';

export function TutorAdminLessonEditPage() {
  const { lessonId } = useParams({ strict: false }) as { lessonId: string };
  const navigate = useNavigate();
  const { data: lesson, isLoading } = useAdminLessonDetail(lessonId);
  const updateLesson = useUpdateLesson();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [contentMd, setContentMd] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [displayOrder, setDisplayOrder] = useState(1);
  const [status, setStatus] = useState<ContentStatus>('draft');
  const [seeded, setSeeded] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (lesson && !seeded) {
      setTitle(lesson.title);
      setSlug(lesson.slug);
      setContentMd(lesson.content_md);
      setVideoUrl(lesson.video_url ?? '');
      setDisplayOrder(lesson.display_order);
      setStatus(lesson.status);
      setSeeded(true);
    }
  }, [lesson, seeded]);

  function handleTitleChange(val: string) {
    setTitle(val);
    if (!slugTouched) setSlug(slugify(val));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    await updateLesson.mutateAsync({
      id: lessonId,
      chapter_id: lesson?.chapter_id,
      title: title.trim(),
      slug: slug.trim(),
      content_md: contentMd,
      video_url: videoUrl.trim() || null,
      display_order: displayOrder,
      status,
    });
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      navigate({ to: '/tutor/admin/lessons/$lessonId', params: { lessonId } });
    }, 800);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="p-6 text-center">
        <p className="font-display text-ink-600 font-semibold">Leçon introuvable.</p>
        <Link
          to="/tutor/admin/chapters"
          className="text-primary-600 mt-2 inline-block text-[13px] underline"
        >
          Retour aux chapitres
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <Link
        to="/tutor/admin/lessons/$lessonId"
        params={{ lessonId }}
        className="text-ink-500 hover:text-ink-900 mb-5 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Retour à la leçon
      </Link>

      <form onSubmit={handleSave}>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="font-display text-ink-900 truncate text-[22px] font-extrabold">
            {title || 'Leçon sans titre'}
          </h1>
          <Button
            type="submit"
            size="sm"
            disabled={updateLesson.isPending}
            className="flex min-h-[2.75rem] shrink-0 items-center gap-1.5"
          >
            <Save className="h-3.5 w-3.5" />
            {saved ? 'Enregistré !' : updateLesson.isPending ? '...' : 'Enregistrer'}
          </Button>
        </div>

        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="col-span-2 flex flex-col gap-1.5 sm:col-span-1">
            <label className="font-display text-ink-700 text-[12px] font-semibold">Titre</label>
            <input
              type="text"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 rounded-xl border px-3 py-2 font-sans text-[13px] focus:outline-none focus:ring-2"
              required
            />
          </div>
          <div className="col-span-2 flex flex-col gap-1.5 sm:col-span-1">
            <label className="font-display text-ink-700 text-[12px] font-semibold">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 rounded-xl border px-3 py-2 font-mono text-[13px] focus:outline-none focus:ring-2"
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="font-display text-ink-700 text-[12px] font-semibold">Ordre</label>
            <input
              type="number"
              min={1}
              value={displayOrder}
              onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 1)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 rounded-xl border px-3 py-2 font-sans text-[13px] focus:outline-none focus:ring-2"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="font-display text-ink-700 text-[12px] font-semibold">Statut</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as ContentStatus)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 rounded-xl border bg-white px-3 py-2 font-sans text-[13px] focus:outline-none focus:ring-2"
            >
              <option value="draft">Brouillon</option>
              <option value="published">Publié</option>
              <option value="archived">Archivé</option>
            </select>
          </div>
        </div>

        <div className="mb-5 flex flex-col gap-1.5">
          <label className="font-display text-ink-700 text-[12px] font-semibold">
            URL vidéo <span className="text-ink-400 font-normal">(optionnel)</span>
          </label>
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://..."
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 rounded-xl border px-3 py-2 font-sans text-[13px] focus:outline-none focus:ring-2"
          />
        </div>

        <div className="mb-2 flex items-center justify-between">
          <label className="font-display text-ink-700 text-[12px] font-semibold">
            Contenu (Markdown)
          </label>
          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            className="text-primary-600 hover:text-primary-800 flex items-center gap-1.5 font-sans text-[12px] transition-colors"
          >
            {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {showPreview ? 'Masquer aperçu' : 'Aperçu'}
          </button>
        </div>

        <div className={showPreview ? 'grid grid-cols-1 gap-4 md:grid-cols-2' : ''}>
          <textarea
            value={contentMd}
            onChange={(e) => setContentMd(e.target.value)}
            rows={24}
            placeholder="# Titre de la leçon&#10;&#10;Contenu en Markdown..."
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full resize-y rounded-xl border px-3 py-2.5 font-mono text-[13px] focus:outline-none focus:ring-2"
          />
          {showPreview && (
            <div className="border-ink-200 min-h-[200px] overflow-auto rounded-xl border bg-white px-4 py-3">
              {contentMd ? (
                <MarkdownMath className="text-ink-800 text-[13px]">{contentMd}</MarkdownMath>
              ) : (
                <p className="text-ink-400 font-sans text-[13px] italic">Aperçu vide</p>
              )}
            </div>
          )}
        </div>

        {updateLesson.isError && (
          <p className="text-accent-600 mt-3 font-sans text-[13px]">
            {updateLesson.error?.message}
          </p>
        )}
      </form>
    </div>
  );
}
